import { Injectable } from '@angular/core';
import { NetworkService } from './network-service';
import { firstValueFrom, forkJoin, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IndexDbHelpMethod } from './index-db-help-method';
import { SharedService } from './shared.service';
import { FormService } from './form.service';
import { ToastService } from './toast.service';
import { ApiEndpoints } from '../enums/api.enum';

@Injectable({
  providedIn: 'root'
})
export class SyncService {
  /** Emits after a sync run that submitted at least one offline form, so form lists can reload. */
  private readonly formsSyncedSubject = new Subject<void>();
  readonly formsSynced$ = this.formsSyncedSubject.asObservable();

  constructor(
    private networkService: NetworkService,
    private idbHelper: IndexDbHelpMethod,
    private sharedService: SharedService,
    private formService: FormService,
    private toastService: ToastService
  ) { }

  init() {
    this.networkService.getStatus().subscribe(online => {
      if (online) {
        this.syncOfflineForms();
      }
    });
  }

  async syncOfflineForms() {
    // Check if online before syncing
    if (!this.networkService.isOnline()) {
      this.toastService.showToast({
        type: 'warn',
        summary: 'Offline',
        detail: 'Cannot sync forms. Please check your internet connection.',
        life: 3000
      });
      return;
    }

    try {
      // Get all offline forms from IndexedDB
      const offlineForms = await firstValueFrom(this.idbHelper.getAllOfflineForms());

      if (!offlineForms || offlineForms.length === 0) {
        this.toastService.showToast({
          type: 'info',
          summary: 'No Offline Forms',
          detail: 'There are no forms to sync.',
          life: 3000
        });
        return;
      }

      this.toastService.showToast({
        type: 'info',
        summary: 'Syncing Forms',
        detail: `Syncing ${offlineForms.length} form(s)...`,
        life: 3000
      });

      // Process each form
      let successCount = 0;
      let errorCount = 0;

      for (const offlineForm of offlineForms) {
        try {
          await this.syncSingleForm(offlineForm);
          successCount++;
        } catch (error) {
          console.error('Error syncing form:', error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        this.formsSyncedSubject.next();
      }

      // Show summary
      if (successCount > 0) {
        this.toastService.showToast({
          type: 'success',
          summary: 'Sync Complete',
          detail: `Successfully synced ${successCount} form(s).${errorCount > 0 ? ` ${errorCount} form(s) failed.` : ''}`,
          life: 5000
        });
      } else if (errorCount > 0) {
        this.toastService.showToast({
          type: 'error',
          summary: 'Sync Failed',
          detail: `Failed to sync ${errorCount} form(s). Please try again.`,
          life: 5000
        });
      }
    } catch (err) {
      console.error('Error syncing offline forms:', err);
      this.toastService.showToast({
        type: 'error',
        summary: 'Sync Error',
        detail: 'An error occurred while syncing forms. Please try again.',
        life: 5000
      });
    }
  }

  private async syncSingleForm(offlineForm: any): Promise<void> {
    const { userForm, signatureBlobs } = offlineForm;

    // Step 1: Convert signature blobs to URLs
    const updatedFormData = await this.convertSignatureBlobsToUrls(userForm, signatureBlobs || {});
    // delete the recordID before submission
    if (updatedFormData?.recordID) {
      delete updatedFormData.recordID;
    }
    // Step 2: Submit the form
    await firstValueFrom(this.formService.submitOrDraftTemplate(updatedFormData));

    // Step 3: Delete from IndexedDB on success
    await this.idbHelper.deleteOfflineForm(offlineForm.id ?? userForm.recordID);
  }

  private async convertSignatureBlobsToUrls(
    formData: any,
    signatureBlobs: { [key: string]: string }
  ): Promise<any> {
    // Create a deep copy to avoid mutating the original
    const updatedFormData = JSON.parse(JSON.stringify(formData));

    // If no signature blobs, return form data as is
    if (!signatureBlobs || Object.keys(signatureBlobs).length === 0) {
      return updatedFormData;
    }

    // Convert all signature blobs to URLs
    const signatureUploadPromises: Promise<{ key: string; url: string }>[] = [];

    for (const [key, dataUrl] of Object.entries(signatureBlobs)) {
      // Only upload if it's a data URL (blob), skip if it's already a URL
      if (dataUrl && dataUrl.startsWith('data:')) {
        signatureUploadPromises.push(
          this.uploadSignatureBlob(dataUrl).then(url => ({ key, url }))
            .catch(error => {
              console.error(`Failed to upload signature blob for key ${key}:`, error);
              throw error; // Re-throw to handle in syncSingleForm
            })
        );
      }
    }

    // Wait for all signature uploads to complete
    if (signatureUploadPromises.length > 0) {
      const signatureUrls = await Promise.all(signatureUploadPromises);
      const signatureUrlMap = new Map(signatureUrls.map(s => [s.key, s.url]));

      // Update form data with signature URLs
      if (updatedFormData.comments_signatures && Array.isArray(updatedFormData.comments_signatures)) {
        updatedFormData.comments_signatures.forEach((entry: any) => {
          if (entry.signatureKey && signatureUrlMap.has(entry.signatureKey)) {
            entry.signature = signatureUrlMap.get(entry.signatureKey);
            // Remove the signatureKey as it's no longer needed
            delete entry.signatureKey;
          }
        });
      }
    }

    return updatedFormData;
  }

  private async uploadSignatureBlob(dataUrl: string): Promise<string> {
    // Convert data URL to Blob
    const blob = this.dataURLtoBlob(dataUrl);
    const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });

    // Upload the file
    const response = await firstValueFrom(this.sharedService.uploadFile(file, file.name, 'dm-signature'));
    const fileUrl = response?.data?.fileUrl;

    if (!fileUrl) {
      throw new Error('Failed to get file URL from upload response');
    }

    return fileUrl;
  }

  private dataURLtoBlob(dataurl: string): Blob {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }
}


